import { appStateForm, FORM_INITIAL_STATE, FormState } from './form.state';
import * as formActions from './form.actions';
import { cloneDeep, isArray } from 'lodash-es';
import { Action, createReducer, createSelector, MemoizedSelector, on } from '@ngrx/store';
import { AppState } from '../../store/models/app.state';
import { QuestionModel } from '../../question/question.model';
import { FormModel } from '../form.model';
import { ControlModel } from '../../question/control.model';

const reducer = createReducer(
  appStateForm.form,
  on(formActions.updateFormList, (state, { formList }) => ({ ...state, list: formList })),
  on(formActions.removeForm, (state, { id }) => {
    const index = state.list.findIndex(form => form.id === id);
    return index > -1 ? { ...state, list: state.list.slice(0, index).concat(state.list.slice(index + 1)) } : state;
  }),
  on(formActions.resetCurrentForm, state => {
    return {
      ...state,
      current: {
        ...state.current,
        form: FORM_INITIAL_STATE(),
        formSnapshot: FORM_INITIAL_STATE(),
        formInPrevVersion: FORM_INITIAL_STATE(),
      },
    };
  }),
  on(formActions.populateCurrentForm, (state, { form }) => ({ ...state, current: { ...state.current, form: form } })),
  on(formActions.populateCurrentFormSnapshot, (state, { form }) => ({
    ...state,
    current: { ...state.current, formSnapshot: cloneDeep(form) },
  })),
  on(formActions.updateFormField, (state, { value, field }) => ({
    ...state,
    current: {
      ...state.current,
      form: { ...state.current.form, [field]: value },
    },
  })),
  on(formActions.pendingCreateUpdateForm, (state, { pending }) => ({
    ...state,
    current: { ...state.current, pending: pending },
  })),
  on(formActions.dispatchedCreateUpdateForm, (state, { dispatched }) => ({
    ...state,
    current: { ...state.current, dispatched: dispatched },
  })),
  on(formActions.successCreateUpdateForm, (state, { success }) => ({
    ...state,
    current: { ...state.current, success: success },
  })),
  on(formActions.formAddQuestion, (state, { question }) => {
    const questions = state.current.form.questions;
    const addQuestionIndex = questions.findIndex(d => (d.id ? d.id === question.id : d === question.id));
    if (addQuestionIndex === -1) {
      questions.push(question.id);
    }
    const questionsPopulated = state.current.form.questionsPopulated || {};
    questionsPopulated[question.id] = question;

    return {
      ...state,
      current: {
        ...state.current,
        form: {
          ...state.current.form,
          questions: questions,
          questionsPopulated: questionsPopulated,
        },
      },
    };
  }),
  on(formActions.formUpdateQuestion, (state, { question }) => {
    return {
      ...state,
      current: {
        ...state.current,
        form: {
          ...state.current.form,
          questionsPopulated: updateQuestionPopulated(state.current.form.questionsPopulated, question),
        },
      },
    };
  }),
  on(formActions.formUpdateQuestions, (state, { questions }) => {
    return {
      ...state,
      current: {
        ...state.current,
        form: {
          ...state.current.form,
          questionsPopulated: updateQuestionPopulated(state.current.form.questionsPopulated, questions),
        },
      },
    };
  }),
  on(formActions.formRemoveQuestions, (state, { ids }) => {
    const questions: number[] = state.current.form.questions.filter(id => !ids.includes(id));
    const questionsPopulated = Object.fromEntries(
      Object.values<QuestionModel>(state.current.form.questionsPopulated)
        .filter(q => !ids.includes(q.id))
        .map(q => [q.id, q]),
    );

    return {
      ...state,
      current: {
        ...state.current,
        form: {
          ...state.current.form,
          questions,
          questionsPopulated,
          body: {
            ...state.current.form.body,
            pages: state.current.form.body.pages.map(page => {
              if (page.isDeleted) {
                return page;
              }

              return {
                ...page,
                questions: page.questions
                  .filter(q => !ids.includes(q.id))
                  .sort((a, b) => a.order - b.order || 0)
                  .map((q, i) => ({
                    ...q,
                    order: i,
                  })),
              };
            }),
          },
        },
      },
    };
  }),
  on(formActions.updateFormInList, (state, { form }) => {
    const formIndex = state.list.findIndex(d => d.id === form.id);
    return formIndex > -1
      ? {
          ...state,
          list: [...[...state.list.slice(0, formIndex)], { ...form }, ...[...state.list.slice(formIndex + 1)]],
        }
      : state;
  }),
  on(formActions.checkCurrentFormForPendingChanges, (state, { changes }) => ({
    ...state,
    current: { ...state.current, pendingChanges: changes },
  })),
  on(formActions.populateFormRules, (state, { formRules }) => {
    const filtered = formRules; /*.filter(r => !r.isDeleted)*/
    return {
      ...state,
      current: { ...state.current, formRules: cloneDeep(filtered), formRulesSnapshot: cloneDeep(filtered) },
    };
  }),
  on(formActions.populateFormIntegrations, (state, { integrations }) => {
    const filtered = integrations.filter(i => !i.isDeleted);
    return {
      ...state,
      current: { ...state.current, integrations: cloneDeep(filtered) },
    };
  }),
  on(formActions.populateRuleInList, (state, { rule }) => ({
    ...state,
    current: { ...state.current, formRules: [...state.current.formRules, rule] },
  })),
  on(formActions.removeRuleFromList, (state, { ruleId }) => ({
    ...state,
    current: { ...state.current, formRules: [...state.current.formRules].filter(r => r.id !== ruleId) },
  })),
  on(formActions.removeRulesFromList, (state, { ruleIds }) => ({
    ...state,
    current: { ...state.current, formRules: [...state.current.formRules].filter(r => !ruleIds.includes(r.id)) },
  })),
  on(formActions.updateFormIntegrationInList, (state, { integration }) => {
    const index = state.current.integrations.findIndex(i => i.id === integration.id);
    if (index > -1) {
      state.current.integrations[index] = integration;
    } else {
      state.current.integrations.push(integration);
    }
    return state;
  }),
  on(formActions.updateFormIntegrationsInList, (state, { integrations }) => {
    const merged = [...state.current.integrations.filter(e => !integrations.some(i => i.id === e.id)), ...integrations];

    return {
      ...state,
      current: {
        ...state.current,
        integrations: merged,
      },
    };
  }),
  on(formActions.populateFormInPrevVersion, (state, { form }) => {
    return { ...state, current: { ...state.current, formInPrevVersion: form } };
  }),
  on(formActions.resetFormList, state => {
    return { ...state, list: [] };
  }),
);

function updateQuestionPopulated(
  questionsPopulated: Record<number, QuestionModel>,
  question: QuestionModel | QuestionModel[],
): Record<number, QuestionModel> {
  if (isArray(question)) {
    question.forEach((q: QuestionModel) => {
      if (questionsPopulated[q.id]) {
        questionsPopulated[q.id] = q;
      }
    });
  } else {
    if (questionsPopulated[question.id]) {
      questionsPopulated[question.id] = question;
    }
  }

  return questionsPopulated;
}

export function formReducer(state: FormState, action: Action): FormState {
  return reducer(state, action);
}

export const selectForm = (state: AppState) => state.form;
export const selectFormPrevVersion: MemoizedSelector<AppState, FormModel> = createSelector(
  selectForm,
  formState => formState.current.formInPrevVersion,
);
export const selectFormList = createSelector(selectForm, formState => formState.list);
export const selectQuestionControlInPrevFormVersion = (questionGuid: string, question_uuid: string) =>
  createSelector(selectFormPrevVersion, form => {
    const question: QuestionModel | undefined = Object.values(form.questionsPopulated).find(
      (q: QuestionModel) => q.questionGuid === questionGuid,
    ) as QuestionModel;
    if (!question) return null;
    return question.controls.find((c: ControlModel) => c.question_uuid === question_uuid);
  });
